<template>
  <main class="ticket-conversation">
    <header>
      <div><h3>Conversation</h3><span>{{ messageCount }} messages</span></div>
      <button v-if="hasOlderMessages" type="button" :disabled="loadingMessages" @click="$emit('load-older')">
        {{ loadingMessages ? 'Loading…' : 'Load older messages' }}
      </button>
    </header>
    <div ref="messageList" class="ticket-conversation__messages">
      <app-no-results v-if="!messages.length" design="new" small title="No messages" text="Start the conversation below." />
      <article v-for="message in messages" :key="message.id" class="ticket-message" :class="{ 'ticket-message--support': !isRequesterMessage(message) }">
        <header><strong>{{ message.author ? message.author.full_name : 'Former user' }}</strong><time>{{ formatDateTime(message.created_at) }}</time></header>
        <p>{{ message.body }}</p>
        <support-file-preview-list v-if="message.attachments.length" message :files="message.attachments" @remove="$emit('delete-attachment', $event.file)" />
      </article>
    </div>
    <form class="ticket-composer" @submit.prevent="$emit('send')">
      <app-textarea :value="newMessage" design="new" :disabled="closed" :placeholder="closed ? 'Reopen the ticket to continue the conversation.' : 'Write a clear reply…'" @input="$emit('update:new-message', $event)" @paste.native="pasteFiles" />
      <support-file-preview-list v-if="files.length" queued :files="files" @remove="$emit('remove-file', $event.index)" />
      <div class="ticket-composer__footer">
        <div class="ticket-attachment-picker">
          <span>Attachments</span>
          <label class="ticket-composer__attach"><input type="file" multiple :disabled="closed" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv" @change="$emit('select-files', $event)"><i>+ Add files</i></label>
        </div>
        <app-button design="new" theme="success" type="submit" :disabled="working || closed || !newMessage">Send reply</app-button>
      </div>
    </form>
  </main>
</template>

<script>
import { formatDateTime } from '../ticketState'
import { clipboardSupportFiles } from '../supportFileUploads'
import SupportFilePreviewList from './SupportFilePreviewList'

export default {
  name: 'SupportTicketConversationPanel',
  components: { SupportFilePreviewList },
  props: {
    messages: { type: Array, required: true },
    messageCount: { type: Number, required: true },
    requesterId: { type: [Number, String], default: null },
    hasOlderMessages: Boolean,
    loadingMessages: Boolean,
    newMessage: { type: String, default: '' },
    files: { type: Array, default: () => [] },
    closed: Boolean,
    working: Boolean
  },
  methods: {
    formatDateTime,
    pasteFiles(event) {
      const files = clipboardSupportFiles(event)
      if (files.length) this.$emit('select-files', files)
    },
    isRequesterMessage(message) { return Boolean(message.author && message.author.id === this.requesterId) },
    scrollToBottom() {
      const list = this.$refs.messageList
      if (list) list.scrollTop = list.scrollHeight
    },
    scrollHeight() {
      const list = this.$refs.messageList
      return list ? list.scrollHeight : 0
    },
    restoreScrollAfterPrepend(previousHeight) {
      const list = this.$refs.messageList
      if (list) list.scrollTop = list.scrollHeight - previousHeight
    }
  }
}
</script>

<style lang="scss" scoped>
.ticket-conversation { display: flex; min-width: 0; flex-direction: column; overflow: hidden; border: 1px solid $ui-line; border-radius: 16px; background: $ui-surface; box-shadow: 0 8px 26px rgba(28, 45, 74, .055); }
.ticket-conversation > header { display: flex; min-height: 52px; align-items: center; justify-content: space-between; padding: 20px 20px 14px; }
.ticket-conversation > header div { display: flex; align-items: baseline; gap: $ui-space-8; }
.ticket-conversation h3 { margin: 0; color: $ui-ink; font-family: $ui-font-heading; font-size: 16px; font-weight: 600; }
.ticket-conversation > header span { color: $ui-muted; font-size: 14px; }
.ticket-conversation > header button { border: 0; background: transparent; color: $ui-blue; cursor: pointer; font-weight: 600; }
.ticket-conversation__messages { display: flex; min-height: 260px; flex-direction: column; gap: 10px; margin: 0 20px; padding: 16px; border: 1px solid #e5eaf1; border-radius: 14px; background: #f7f9fc; }
.ticket-message { align-self: flex-start; width: 92%; padding: $ui-space-12; border: 1px solid $ui-line; border-radius: 4px $ui-card-radius $ui-card-radius $ui-card-radius; background: $ui-surface; box-shadow: $ui-card-shadow; }
.ticket-message--support { align-self: flex-end; border-color: rgba($ui-blue, .2); border-radius: $ui-card-radius 4px $ui-card-radius $ui-card-radius; background: $ui-blue-soft; }
.ticket-message header { display: flex; justify-content: space-between; gap: $ui-space-12; color: $ui-muted; font-size: $ui-font-size-metadata; }
.ticket-message header strong { color: $ui-ink; }
.ticket-message p { margin: $ui-space-8 0 0; color: $ui-text; font-size: $ui-font-size-body; line-height: 1.55; white-space: pre-wrap; overflow-wrap: anywhere; }
.ticket-message__attachments { display: flex; flex-wrap: wrap; gap: $ui-space-8; margin-top: $ui-space-12; }
.ticket-message__attachments a { display: grid; gap: 2px; padding: $ui-space-8 $ui-space-12; border: 1px solid $ui-line-strong; border-radius: $ui-control-radius; background: $ui-surface; color: $ui-text; font-size: $ui-font-size-metadata; text-decoration: none; }
.ticket-message__attachments a span { color: $ui-blue; font-weight: 700; }
.ticket-composer { padding: 16px 20px 20px; background: $ui-surface; }
.ticket-composer :deep(.textarea) { min-height: 64px; }
.ticket-composer__footer { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-12; margin-top: $ui-space-8; }
.ticket-composer__attach { color: $ui-blue; cursor: pointer; font-size: $ui-font-size-metadata; font-weight: 600; }
.ticket-composer__attach input { position: absolute; width: 1px; height: 1px; overflow: hidden; }
.ticket-attachment-picker { display: inline-flex; align-items: center; gap: $ui-space-8; color: $ui-muted; font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
.ticket-attachment-picker i { font-style: normal; letter-spacing: 0; text-transform: none; }
.ticket-composer__files { display: flex; flex-wrap: wrap; gap: $ui-space-8; margin-top: $ui-space-8; }
.ticket-composer__files li { padding: $ui-space-4 $ui-space-8; border-radius: $ui-control-radius; background: $ui-surface-soft; font-size: $ui-font-size-metadata; }
.ticket-composer__files button { border: 0; background: transparent; color: $ui-red; cursor: pointer; }
@media (max-width: 780px) {
  .ticket-message { width: 88%; }
  .ticket-conversation__messages { margin-right: 16px; margin-left: 16px; padding: $ui-space-16; }
  .ticket-composer { padding: $ui-space-12 $ui-space-16; }
}
</style>
